/**
 * Table View Renderer
 * 
 * Renders tabular data with HTML table.
 * Supports sorting and basic styling.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderTableView: VisualizationRenderer = (data) => {
  // Handle array of objects (most common case)
  if (Array.isArray(data) && data.length > 0) {
    // Get column names from first object
    const columns = Object.keys(data[0]);
    
    if (columns.length === 0) {
      return renderEmptyState('No data to display');
    }

    // Build table header
    const headerCells = columns.map(col => `
      <th style="
        padding: ${designTokens.padding.sm} ${designTokens.padding.md};
        text-align: left;
        font-weight: 600;
        font-size: ${designTokens.fontSize.sm};
        color: ${designTokens.contentColor};
        border-bottom: 2px solid ${designTokens.borderColor};
        background: rgba(255, 255, 255, 0.05);
        cursor: pointer;
        user-select: none;
      " data-column="${escapeHtml(col)}">${escapeHtml(col)}</th>
    `).join('');

    // Build table rows
    const rows = data.map((row, index) => {
      const cells = columns.map(col => {
        const value = row[col];
        const displayValue = value === null || value === undefined 
          ? '<em style="color: rgba(255, 255, 255, 0.3);">—</em>'
          : escapeHtml(String(value));
        
        return `
          <td style="
            padding: ${designTokens.padding.sm} ${designTokens.padding.md};
            font-size: ${designTokens.fontSize.sm};
            color: ${designTokens.contentColor};
            border-bottom: 1px solid ${designTokens.borderColor};
          ">${displayValue}</td>
        `;
      }).join('');

      return `
        <tr style="
          background: ${index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)'};
        ">${cells}</tr>
      `;
    }).join('');

    return `
      <div style="
        width: 100%;
        overflow-x: auto;
      ">
        <table style="
          width: 100%;
          border-collapse: collapse;
          font-size: ${designTokens.fontSize.sm};
        ">
          <thead>
            <tr>${headerCells}</tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
      <script>
        (function() {
          // Add sorting functionality
          const headers = document.querySelectorAll('th[data-column]');
          headers.forEach(header => {
            header.addEventListener('click', function() {
              const column = this.getAttribute('data-column');
              const table = this.closest('table');
              const tbody = table.querySelector('tbody');
              const rows = Array.from(tbody.querySelectorAll('tr'));
              
              // Determine sort direction
              const isAscending = this.classList.contains('sort-asc');
              
              // Remove sort classes from all headers
              headers.forEach(h => {
                h.classList.remove('sort-asc', 'sort-desc');
              });
              
              // Sort rows
              rows.sort((a, b) => {
                const aVal = a.querySelector('td:nth-child(' + (Array.from(headers).indexOf(this) + 1) + ')')?.textContent || '';
                const bVal = b.querySelector('td:nth-child(' + (Array.from(headers).indexOf(this) + 1) + ')')?.textContent || '';
                
                // Try numeric comparison
                const aNum = parseFloat(aVal);
                const bNum = parseFloat(bVal);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                  return isAscending ? bNum - aNum : aNum - bNum;
                }
                
                // String comparison
                return isAscending 
                  ? bVal.localeCompare(aVal)
                  : aVal.localeCompare(bVal);
              });
              
              // Re-append sorted rows
              rows.forEach(row => tbody.appendChild(row));
              
              // Add sort class
              this.classList.add(isAscending ? 'sort-desc' : 'sort-asc');
            });
            
            // Add hover effect
            header.style.transition = 'background 0.2s';
            header.addEventListener('mouseenter', function() {
              this.style.background = 'rgba(255, 255, 255, 0.1)';
            });
            header.addEventListener('mouseleave', function() {
              this.style.background = 'rgba(255, 255, 255, 0.05)';
            });
          });
        })();
      </script>
    `;
  }

  // Handle single object (convert to key-value table)
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const entries = Object.entries(data);
    
    if (entries.length === 0) {
      return renderEmptyState('No data to display');
    }

    const rows = entries.map(([key, value]) => {
      const displayValue = value === null || value === undefined
        ? '<em style="color: rgba(255, 255, 255, 0.3);">—</em>'
        : escapeHtml(typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value));
      
      return `
        <tr>
          <td style="
            padding: ${designTokens.padding.sm} ${designTokens.padding.md};
            font-weight: 600;
            font-size: ${designTokens.fontSize.sm};
            color: ${designTokens.contentColor};
            border-bottom: 1px solid ${designTokens.borderColor};
            width: 30%;
          ">${escapeHtml(key)}</td>
          <td style="
            padding: ${designTokens.padding.sm} ${designTokens.padding.md};
            font-size: ${designTokens.fontSize.sm};
            color: ${designTokens.contentColorMuted};
            border-bottom: 1px solid ${designTokens.borderColor};
            word-break: break-word;
          ">${displayValue}</td>
        </tr>
      `;
    }).join('');

    return `
      <table style="
        width: 100%;
        border-collapse: collapse;
        font-size: ${designTokens.fontSize.sm};
      ">
        <tbody>
          ${rows}
        </tbody>
      </table>
    `;
  }

  return renderEmptyState('Invalid data format for table view');
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


